import React, { useState, useEffect } from "react";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import { Request } from "../../../hooks/Request";
import { useModal } from "../../../contexts/ModalContext";
import { CalendarFormModal, DATE_FIELDS } from "./CalendarFormModal";
import { Button } from "../../../components/Buttons";

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const { openModal, onClose } = useModal();

  const startDate = format(startOfMonth(currentDate), "MM-dd-yyyy");
  const endDate = format(endOfMonth(currentDate), "MM-dd-yyyy");
  const params = new URLSearchParams();
  params.append("StartDate", startDate);
  params.append("EndDate", endDate);
  params.append("FieldDates", "etd");
  params.append("FieldDates", "eta");
  params.append("FieldDates", "fechaRequerimientoDam");
  params.append("FieldDates", "fechaEstimadaDePagoDim");
  params.append("FieldDates", "fechaConfirmadaPagoDim");
  params.append("FieldDates", "fechaDeDim");
  params.append("FieldDates", "fechaVencimientoDim(Ritex)");
  params.append("FieldDates", "fechaNacionalización");
  params.append("FieldDates", "fechaPagoTributosAduaneros");
  params.append("FieldDates", "fechaAsignaciónDeCanal");
  params.append("FieldDates", "fechaPaseDeSalida");
  params.append("FieldDates", "fechaOrdenDeRecepción");
  params.append("FieldDates", "fechaLimiteDevoluciónContenedores");
  params.append("FieldsReturn", "codigoDeInterno");

  const {
    data: rawEvents,
    loading,
    call,
  } = Request({
    urlApi: `/Calendar?${params.toString()}`,
  });

  let events = [];
  if (Array.isArray(rawEvents)) {
    events = rawEvents;
  } else if (rawEvents && Array.isArray(rawEvents.data)) {
    events = rawEvents.data;
  } else if (rawEvents && Array.isArray(rawEvents.items)) {
    events = rawEvents.items;
  }

  useEffect(() => {
    call();
  }, [currentDate]);

  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  const handleAddEvent = (date) => {
    openModal(
      <CalendarFormModal date={date} onClose={onClose} reload={() => call()} />,
    );
  };

  const handleEditEvent = (ev, e) => {
    e.stopPropagation(); // Evitar disparar handleAddEvent del contenedor
    openModal(
      <CalendarFormModal event={ev} onClose={onClose} reload={() => call()} />,
    );
  };

  const getFieldLabel = (fieldName) => {
    const field = DATE_FIELDS.find((f) => f.name === fieldName);
    return field ? field.label : fieldName;
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={handlePrevMonth}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <i className="fas fa-chevron-left text-gray-600"></i>
          </button>
          <h2 className="text-2xl font-semibold text-gray-800 capitalize w-48 text-center">
            {format(currentDate, "MMMM yyyy", { locale: es })}
          </h2>
          <button
            onClick={handleNextMonth}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            <i className="fas fa-chevron-right text-gray-600"></i>
          </button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setView("calendar")}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                view === "calendar"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className="fas fa-calendar-alt mr-2"></i>
              Calendario
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                view === "list"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className="fas fa-list mr-2"></i>
              Lista
            </button>
          </div>
          <Button onClick={() => handleAddEvent(null)} className="py-2.5">
            <i className="fas fa-plus mr-2"></i>
            Registrar Fecha
          </Button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const dateFormat = "EEEE";
    const days = [];
    let startDate = startOfWeek(currentDate, { weekStartsOn: 1 }); // Lunes como primer día

    for (let i = 0; i < 7; i++) {
      days.push(
        <div
          className="text-center font-semibold text-sm text-gray-500 uppercase py-3 border-b border-gray-200"
          key={i}
        >
          {format(addDays(startDate, i), dateFormat, { locale: es })}
        </div>,
      );
    }
    return (
      <div className="grid grid-cols-7 mb-2 bg-gray-50 rounded-t-lg">
        {days}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;

        // Filtrar eventos del día
        const dayEvents = events.filter(
          (ev) =>
            ev.Value && isSameDay(parseISO(ev.Value.split("T")[0]), cloneDay),
        );

        days.push(
          <div
            className={`min-h-[120px] p-2 border-b border-r border-gray-200 bg-white transition-colors relative group
              ${!isSameMonth(day, monthStart) ? "text-gray-400 bg-gray-50" : "text-gray-800"}
              ${isSameDay(day, new Date()) ? "bg-blue-50/30" : "hover:bg-gray-50 cursor-pointer"}
              ${i === 6 ? "border-r-0" : ""}
            `}
            key={day.toString()}
            onClick={() => handleAddEvent(cloneDay)}
          >
            <div className="flex justify-between items-start">
              <span
                className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                ${isSameDay(day, new Date()) ? "bg-blue-600 text-white" : ""}
              `}
              >
                {formattedDate}
              </span>
              <span className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 transition-opacity">
                <i className="fas fa-plus text-xs"></i>
              </span>
            </div>

            <div className="mt-2 flex flex-col gap-1 max-h-[80px] overflow-y-auto scrollbar-hide">
              {dayEvents.map((ev, idx) => (
                <div
                  key={idx}
                  onClick={(e) => handleEditEvent(ev, e)}
                  className="text-[10px] p-1 px-1.5 rounded truncate bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 cursor-pointer transition-colors"
                  title={`${ev.codigoDeInterno || "Sin código"} - ${getFieldLabel(ev.FieldNameDate)}`}
                >
                  <span className="font-semibold">
                    {ev.codigoDeInterno || "Trámite"}:
                  </span>{" "}
                  {getFieldLabel(ev.FieldNameDate)}
                </div>
              ))}
            </div>
          </div>,
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7" key={day.toString()}>
          {days}
        </div>,
      );
      days = [];
    }

    return (
      <div className="border border-gray-200 rounded-b-lg shadow-sm bg-white border-t-0">
        {rows}
      </div>
    );
  };

  const renderListView = () => {
    // Ordenar eventos por fecha
    const sortedEvents = [...events].sort((a, b) => {
      if (!a.Value) return 1;
      if (!b.Value) return -1;
      return new Date(a.Value) - new Date(b.Value);
    });

    if (sortedEvents.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-gray-300 mb-4">
            <i className="far fa-calendar-times text-6xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900">
            No hay fechas registradas
          </h3>
          <p className="text-gray-500 mt-1">
            No se encontraron eventos para este mes.
          </p>
          <Button onClick={() => handleAddEvent(null)} className="mt-4">
            Registrar primera fecha
          </Button>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {sortedEvents.map((ev, idx) => (
            <li
              key={idx}
              className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center justify-center bg-blue-50 text-blue-700 w-14 h-14 rounded-lg font-bold">
                  <span className="text-xs uppercase leading-none">
                    {format(parseISO(ev.Value.split("T")[0]), "MMM", {
                      locale: es,
                    })}
                  </span>
                  <span className="text-xl leading-none mt-1">
                    {format(parseISO(ev.Value.split("T")[0]), "d")}
                  </span>
                </div>
                <div>
                  <h4 className="text-md font-semibold text-gray-800">
                    Trámite: {ev.codigoDeInterno || `ID ${ev.ProcedureId}`}
                  </h4>
                  <p className="text-sm text-gray-500 capitalize mt-0.5">
                    Campo:{" "}
                    <span className="font-medium text-gray-700">
                      {getFieldLabel(ev.FieldNameDate)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm border border-gray-200 px-3 py-1 rounded-full text-gray-600 bg-gray-50">
                  <i className="far fa-calendar mr-2"></i>
                  {format(
                    parseISO(ev.Value.split("T")[0]),
                    "EEEE d 'de' MMMM, yyyy",
                    { locale: es },
                  )}
                </div>
                <Button
                  onClick={(e) => handleEditEvent(ev, e)}
                  className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg"
                >
                  <i className="fas fa-edit"></i>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="p-6 h-full flex flex-col bg-white rounded-lg shadow-md animate-fade-in">
      {renderHeader()}

      <div className="flex-1 relative">
        {!loading && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg backdrop-blur-[1px]">
            <div className="flex flex-col items-center">
              <i className="fas fa-circle-notch fa-spin text-4xl text-blue-600 mb-3"></i>
              <span className="text-gray-600 font-medium animate-pulse">
                Cargando calendario...
              </span>
            </div>
          </div>
        )}

        {view === "calendar" ? (
          <div key="calendar-view" className="animate-fade-in">
            {renderDays()}
            {renderCells()}
          </div>
        ) : (
          <div key="list-view" className="animate-fade-in">
            {renderListView()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
